/**
 * Shared control plane for useRemoteAdvanceTimer — avoids circular imports with QueueController.
 */

import { isTimerBasedRenderer } from './types';
import { getRendererGaplessEnabled, hasRendererGaplessPref } from '../services/gaplessPlaybackSettings';

let cancelTimerFn: (() => void) | null = null;
let prefetchQueueIndex = -1;
let setNextFillConfirmedQueueIndex = -1;
let rendererGaplessAdvanceAt = 0;
/** Updated when Connector emits renderer-position-changed (GetPositionInfo poll). */
let lastRendererPositionAtMs = 0;

/** Stale GetPositionInfo RelTime detection — buffer-and-play renderers freeze RelTime mid-track. */
let lastRelTimeSecs: number | null = null;
let consecutiveStaleRelTimePolls = 0;
let wallClockDueToStaleRelTime = false;

/** Consecutive unchanged RelTime polls before treating RelTime as non-authoritative (~10s at 5s poll). */
export const STALE_REL_TIME_POLL_THRESHOLD = 2;

/** Incremented on every track change — timer, position, and track-ended paths dedupe via this. */
let advanceGeneration = 0;

/** User-initiated stop/pause — suppress auto-advance and stalled-start retry. */
let userTransportStopUntil = 0;
const USER_TRANSPORT_STOP_WINDOW_MS = 60_000;

/** Pending delayed advance from timer/stopped paths — cancelled on generation bump. */
let pendingDelayedAdvanceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingDelayedAdvanceToken: { gen: number; trackId: string } | null = null;

export function markUserTransportStop(): void {
  userTransportStopUntil = Date.now() + USER_TRANSPORT_STOP_WINDOW_MS;
  cancelPendingDelayedAdvance();
}

export function isUserTransportStopRecent(): boolean {
  return userTransportStopUntil > 0 && Date.now() < userTransportStopUntil;
}

export function cancelPendingDelayedAdvance(): void {
  if (pendingDelayedAdvanceTimer) {
    clearTimeout(pendingDelayedAdvanceTimer);
    pendingDelayedAdvanceTimer = null;
  }
  pendingDelayedAdvanceToken = null;
}

/**
 * Schedule a deferred advance only when generation + trackId still match at fire time.
 * Suppression must kill the action — never defer a rejected advance.
 */
export function scheduleValidatedDelayedAdvance(
  delayMs: number,
  token: { gen: number; trackId: string },
  onFire: () => void,
): void {
  cancelPendingDelayedAdvance();
  pendingDelayedAdvanceToken = token;
  pendingDelayedAdvanceTimer = setTimeout(() => {
    pendingDelayedAdvanceTimer = null;
    const scheduled = pendingDelayedAdvanceToken;
    pendingDelayedAdvanceToken = null;
    if (!scheduled) return;
    if (scheduled.gen !== advanceGeneration) return;
    onFire();
  }, delayMs);
}

export function isScheduledAdvanceTokenValid(
  capturedGen: number,
  capturedTrackId: string | null | undefined,
  currentTrackId: string | null | undefined,
): boolean {
  if (capturedGen !== advanceGeneration) return false;
  if (!capturedTrackId || !currentTrackId) return capturedTrackId === currentTrackId;
  return capturedTrackId === currentTrackId;
}

export function bumpAdvanceGeneration(): number {
  advanceGeneration += 1;
  resetStaleRelTimeTracking();
  cancelPendingDelayedAdvance();
  return advanceGeneration;
}

export function getAdvanceGeneration(): number {
  return advanceGeneration;
}

export function registerRemoteAdvanceCancel(fn: () => void): void {
  cancelTimerFn = fn;
}

export function cancelRemoteAdvanceTimer(): void {
  cancelTimerFn?.();
}

export function setGaplessPrefetchIndex(idx: number): void {
  prefetchQueueIndex = idx;
}

export function clearGaplessPrefetchIndex(): void {
  prefetchQueueIndex = -1;
}

export function markSetNextFillConfirmed(queueIndex: number): void {
  setNextFillConfirmedQueueIndex = queueIndex;
}

export function clearSetNextFillConfirmed(): void {
  setNextFillConfirmedQueueIndex = -1;
}

export function isSetNextFillConfirmedForQueueIndex(queueIndex: number): boolean {
  return queueIndex >= 0 && setNextFillConfirmedQueueIndex === queueIndex;
}

export function getSetNextFillConfirmedQueueIndex(): number {
  return setNextFillConfirmedQueueIndex;
}

export function isGaplessPrefetchActive(): boolean {
  return prefetchQueueIndex >= 0;
}

export function getGaplessPrefetchIndex(): number {
  return prefetchQueueIndex;
}

export function markRendererGaplessAdvance(): void {
  rendererGaplessAdvanceAt = Date.now();
}

export function isRendererGaplessAdvanceRecent(withinMs = 120_000): boolean {
  return rendererGaplessAdvanceAt > 0 && Date.now() - rendererGaplessAdvanceAt < withinMs;
}

export function shouldDeferAdvanceToRenderer(zone: any): boolean {
  if (!zone) return false;
  const rendererId = zone.id;
  if (rendererId && hasRendererGaplessPref(rendererId) && !getRendererGaplessEnabled(rendererId)) {
    return false;
  }
  if (zone.metadata?.gapless_enabled === false) return false;
  const transport = String(zone.metadata?.transport_type ?? zone.transport_type ?? '').toLowerCase();
  const isUpnp = transport === 'upnp' || zone.type === 'upnp';
  if (!isUpnp) return false;
  return isGaplessPrefetchActive() || isRendererGaplessAdvanceRecent();
}

export function markRendererPositionReport(): void {
  lastRendererPositionAtMs = Date.now();
}

export function resetStaleRelTimeTracking(): void {
  lastRelTimeSecs = null;
  consecutiveStaleRelTimePolls = 0;
  wallClockDueToStaleRelTime = false;
}

export function isWallClockDueToStaleRelTime(): boolean {
  return wallClockDueToStaleRelTime;
}

/** Stall reconcile: renderer still PLAYING but position frozen — resume wall-clock synthesis. */
export function enableWallClockSynthesisForStall(): void {
  wallClockDueToStaleRelTime = true;
}

/**
 * Returns whether a GetPositionInfo RelTime should update store.position.
 * Unchanged RelTime for N consecutive polls is treated as "no update," not a freeze.
 */
export function recordRendererRelTime(relTimeSecs: number): {
  shouldApplyToStore: boolean;
  stalePollCount: number;
} {
  if (lastRelTimeSecs === null) {
    lastRelTimeSecs = relTimeSecs;
    consecutiveStaleRelTimePolls = 0;
    return { shouldApplyToStore: true, stalePollCount: 0 };
  }

  if (Math.abs(relTimeSecs - lastRelTimeSecs) < 0.5) {
    consecutiveStaleRelTimePolls += 1;
    if (consecutiveStaleRelTimePolls >= STALE_REL_TIME_POLL_THRESHOLD) {
      wallClockDueToStaleRelTime = true;
      return { shouldApplyToStore: false, stalePollCount: consecutiveStaleRelTimePolls };
    }
    return { shouldApplyToStore: true, stalePollCount: consecutiveStaleRelTimePolls };
  }

  lastRelTimeSecs = relTimeSecs;
  consecutiveStaleRelTimePolls = 0;
  wallClockDueToStaleRelTime = false;
  return { shouldApplyToStore: true, stalePollCount: 0 };
}

/** True when the Connector has recently reported renderer position (GetPositionInfo). */
export function isRendererPositionReportingActive(staleMs = 20_000): boolean {
  return lastRendererPositionAtMs > 0 && Date.now() - lastRendererPositionAtMs < staleMs;
}

/**
 * Primary wall-clock advance is for timer-based renderers (Ideon/MU2/AirLens-class)
 * and browser-only paths without Connector position polling.
 * Position-reporting UPnP renderers (Naim, Lumin, Sonos, …) advance on
 * renderer-track-ended from the Connector monitor instead.
 */
export function shouldUseWallClockAdvance(
  zone: any,
  renderer: { id: string; name?: string } | null | undefined,
): boolean {
  if (shouldDeferAdvanceToRenderer(zone)) return false;
  if (wallClockDueToStaleRelTime) return true;
  const isTauri = typeof window !== 'undefined' && !!(window as any).__TAURI__;
  if (isTauri && renderer && !isTimerBasedRenderer(renderer, zone)) {
    return false;
  }
  return true;
}

/** Margin after expected track end before a silent-renderer fallback may advance. */
export const WALL_CLOCK_FALLBACK_MARGIN_SECS = 45;
